"use client";
import { CourseVideo } from "@/components/CourseContent/ContentOverview/types";
import {
  ElementClickedWebElement,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import React, { useState } from "react";

import { ComponentItem } from "@/utils/getSortedComponentsBySectionOrder";
import { ProgramHeroSideRailContainerProps } from "./types";
import SideRailTitle from "../../ProgramDetailRefresh/SideRail/SideRailTitle";
import SideRailVideoPlayer from "../../ProgramDetailRefresh/SideRail/SideRailVideoPlayer";
import SideRailImage from "../../ProgramDetailRefresh/SideRail/SideRailImage";
import SideRailRating from "../../ProgramDetailRefresh/SideRail/SideRailRating";
import ProgramPricing from "../../ProgramDetailRefresh/ProgramPricing";
import ProgramEnrollButton from "../../ProgramDetailRefresh/ProgramEnrollButton";
import ProgramCourses from "../ProgramCourses";
import BulletListInfoSidebar from "../BulletListInfo";
import ProgramEndorsements from "../../ProgramDetailRefresh/ProgramEndorsements";
import PartnerLogo from "../PartnerLogo";
import IndustryInsights from "../IndustryInsights";
import StatisticComponent from "../StatisticComponent";
import SideRailImageTitle from "../../ProgramDetailRefresh/SideRail/SideRailImageTitle";

export default function useSideRailComponentList({
  programData,
  courses,
  pricingData,
  skus,
  programIsEnrollable,
}: ProgramHeroSideRailContainerProps) {
  const { enrollmentCount, title } = programData;

  const [sideRailElement] = useState<ElementClickedWebElement>({
    elementType: ElementType.Button,
    htmlId: "earn-cert",
    location: "hero-side-rail",
    position: "1",
    text: "",
    name: "course-conversion",
  });

  const { elementClicked } = useElementEvents({
    webElement: sideRailElement,
  });

  const handleCTAEvent = (updatedText: string = "") => {
    const updatedElement = updatedText
      ? { ...sideRailElement, text: updatedText }
      : sideRailElement;

    elementClicked?.({ webElement: updatedElement });
  };
  const programOverviewVideo =
    programData.video.length > 0
      ? (programData.video[0] as CourseVideo)
      : undefined;

  const formattedInsights = programData.jobOutlookItems.map((item) => ({
    value: item.value ?? "",
  }));

  const componentList: ComponentItem[] = [
    {
      key: "sideRailTitle",
      renderer: () => <SideRailTitle key="sideRailTitle" />,
    },
    {
      key: "videoPlayer",
      renderer: () => (
        <SideRailVideoPlayer
          key="videoPlayer"
          title={title}
          video={programOverviewVideo}
        />
      ),
    },
    {
      key: "image",
      renderer: () => <SideRailImage key="image" />,
    },
    {
      key: "rating",
      renderer: () => (
        <SideRailRating key="rating" enrollmentCount={enrollmentCount} />
      ),
    },
    {
      key: "pricing",
      renderer: () =>
        pricingData ? (
          <div
            key="pricing"
            className="flex place-content-center font-bold w-full gap-1"
          >
            <ProgramPricing pricingData={pricingData} />
          </div>
        ) : null,
    },
    {
      key: "ctaButton",
      renderer: () =>
        programIsEnrollable &&
        programData.programEligibleForOneClickPurchase ? (
          <div key="ctaButton">
            <ProgramEnrollButton
              programData={programData}
              courses={courses}
              skus={skus}
              additionalClickEvent={handleCTAEvent}
            />
          </div>
        ) : null,
    },
    {
      key: "programCourses",
      renderer: () =>
        courses.length > 0 ? (
          <ProgramCourses key="programCourses" courses={courses} />
        ) : null,
    },
    {
      key: "bulletList",
      renderer: () => <BulletListInfoSidebar key="bulletList" />,
    },
    {
      key: "stats",
      renderer: () => <StatisticComponent />,
    },
    {
      key: "endorsements",
      renderer: () =>
        programData.corporateEndorsements?.length > 0 ? (
          <ProgramEndorsements
            key="endorsements"
            corporateEndorsements={programData.corporateEndorsements}
            isInSideRail={true}
          />
        ) : null,
    },
    {
      key: "industryInsights",
      renderer: () =>
        formattedInsights?.length > 0 ? (
          <IndustryInsights
            key="industryInsights"
            insights={formattedInsights}
          />
        ) : null,
    },
    {
      key: "partnerLogo",
      renderer: () =>
        programData.partners?.length > 0 ? (
          <PartnerLogo
            key="partnerLogo"
            title={programData.title}
            partners={programData.partners}
          />
        ) : null,
    },
    {
      key: "imageTitle",
      renderer: () => (
        <SideRailImageTitle key="imageTitle" partners={programData.partners} />
      ),
    },
    //Add new components here
  ];

  return componentList;
}
